/** @file prepareFacets: resolves a puppet's impostors, node indices and sector tables once (memoised). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { resolveSectorTable } from '@zilo/pose3d-format/tables/resolveSectorTable';
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import type { FacetEntry, SectorTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { FacetItem, FacetPlan } from '@zilo/pose3d-facet/prepare/facetPlanTypes';

const plans = new WeakMap<Puppet, FacetPlan>();

/** The entry's table and its FLIP twin (the table itself when none); unknown ids throw `SECTOR_TABLE_UNKNOWN`. */
function tablesOf(puppet: Puppet, entry: FacetEntry, path: string): [SectorTable, SectorTable] {
  const table = resolveSectorTable(entry.yaw, puppet) ?? throwError(ERROR_CODES.SECTOR_TABLE_UNKNOWN, { id: entry.yaw }, { path });
  return [table, resolveSectorTable(`${entry.yaw}_FLIP`, puppet) ?? table];
}

/** Per drawn tile of the table: whether the entry's `slots` names an image for it. */
function filledOf(entry: FacetEntry, table: SectorTable): boolean[] {
  return Array.from({ length: tileCountOf(table) }, (_, t) => Object.hasOwn(entry.slots, String(t)));
}

/**
 * The puppet's `FacetPlan`, built on first use and memoised per puppet object (documents are immutable; an edit is a
 * new object). Throws `DRAWABLE_NODE_UNKNOWN` for an impostor on an unknown node and `SECTOR_TABLE_UNKNOWN` (path
 * `/drawables/<i>/facets/<j>/yaw`) for an entry whose table resolves neither in the puppet nor in the built-ins.
 */
export function prepareFacets(puppet: Puppet): FacetPlan {
  const cached = plans.get(puppet);
  if (cached !== undefined) return cached;
  const names = Object.keys(puppet.nodes);
  const nodeIndex = new Map(names.map((n, i) => [n, i]));
  const items: FacetItem[] = [];
  puppet.drawables.forEach((drawable, i) => {
    if (drawable.kind !== 'impostor') return;
    const node = nodeIndex.get(drawable.node)
      ?? throwError(ERROR_CODES.DRAWABLE_NODE_UNKNOWN, { node: drawable.node }, { path: `/drawables/${String(i)}/node` });
    const pairs = drawable.facets.map((e, j) => tablesOf(puppet, e, `/drawables/${String(i)}/facets/${String(j)}/yaw`));
    items.push({
      drawable, nodeIndex: node, tables: pairs.map(([t]) => t), flipTables: pairs.map(([, f]) => f),
      filled: drawable.facets.map((e, j) => filledOf(e, (pairs[j] as [SectorTable, SectorTable])[0])),
    });
  });
  const plan: FacetPlan = {
    puppet, names, nodeIndex, items, active: new Set(),
    scratch: {
      angles: { azimuthDeg: 0, pitchDeg: 0 },
      fold: { yawDeg: 0, pitchDeg: 0, flipped: false },
      choice: { entry: -1, frame: 0, band: -1, folded: false, hidden: false },
      sector: { sector: 0, tile: -1, flip: false, sideBack: 0, drawnAzimuthDeg: 0, drawnScreenAngleDeg: 0 },
      altActive: new Set(),
    },
  };
  plans.set(puppet, plan);
  return plan;
}
